import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  LinearProgress,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { routes } from "../config";
import { useLocalization } from "../contexts/LocalizationContext";
import { LanguageSelector } from "../components/LanguageSelector";
import type { Customer } from "../types";
import CloseIcon from "@mui/icons-material/Close";
// import { useAuth } from '@/auth/AuthContext';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { fetchMemberCoupons, fetchPointsHistory, fetchExpiringPoints } from '@/api';
import type { MemberCoupon, PointAuditHistoryItem, PaginatedResponse, ExpiringItem } from '@/api/types';

interface HomeProps {
  user: Customer;
}

export default function Home({ user }: HomeProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { t, locale } = useLocalization();
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  // const { signOut } = useAuth()
  const { storeSettings } = useStoreSettings();
  const [activeTab, setActiveTab] = useState<'coupon' | 'points'>(
    'coupon'
  );
  const [coupons, setCoupons] = useState<MemberCoupon[]>([]);
  // Track selected coupon for QR generation
  const [selectedCoupon, setSelectedCoupon] = useState<MemberCoupon | null>(null);
  // QR for coupon modal
  const [couponQrDataUrl, setCouponQrDataUrl] = useState<string>("");
  const [pointsPage, setPointsPage] = useState<number>(0);
  const [pointsPageSize] = useState<number>(20);
  const [pointsResp, setPointsResp] = useState<PaginatedResponse<PointAuditHistoryItem> | null>(null);
  const [loadingCoupons, setLoadingCoupons] = useState<boolean>(false);
  const [loadingPoints, setLoadingPoints] = useState<boolean>(false);
  const [showCoupon, setShowCoupon] = useState<boolean>(false);
  const [coupon, setCoupon] = useState<string>("RY278");
  // Expiring points first item
  const [firstExpiring, setFirstExpiring] = useState<ExpiringItem | null>(null);

  // Helper: sign and color by action
  const getPointPresentation = (p: PointAuditHistoryItem) => {
    const action = (p.action || '').toUpperCase();
    const abs = Math.abs(p.points);
    if (action === 'EARN' || action === 'REFUND') {
      return { sign: '+', value: abs, positive: true } as const;
    }
    if (action === 'REDEEM' || action === 'EXPIRE') {
      return { sign: '-', value: abs, positive: false } as const;
    }
    if (action === 'ADJUST') {
      const positive = p.points >= 0;
      return { sign: positive ? '+' : '-', value: Math.abs(p.points), positive } as const;
    }
    // Fallback: use numeric sign
    const positive = p.points >= 0;
    return { sign: positive ? '+' : '-', value: Math.abs(p.points), positive } as const;
  };

  // Define loadPoints at component scope so it can be reused
  const loadPoints = async (pageParam: number = pointsPage) => {
    try {
      setLoadingPoints(true);
      const data = await fetchPointsHistory(pageParam, pointsPageSize);
      setPointsResp(data || null);
    } catch (e) {
      console.error('Failed to load points history', e);
      setPointsResp({ content: [], pageable: { pageNumber: 0, pageSize: pointsPageSize, offset: 0, paged: true, unpaged: false, sort: { sorted: true, unsorted: false, empty: false } }, totalElements: 0, totalPages: 0, last: true, size: pointsPageSize, number: 0, sort: { sorted: true, empty: false, unsorted: false }, numberOfElements: 0, first: true, empty: true });
    } finally {
      setLoadingPoints(false);
    }
  };

  const loadExpiring = async (memberUserId: string) => {
    try {
      const resp = await fetchExpiringPoints(memberUserId);
      const first = resp?.expiring && resp.expiring.length > 0 ? resp.expiring[0] : null;
      setFirstExpiring(first || null);
    } catch (e) {
      console.error('Failed to load expiring points summary', e);
      setFirstExpiring(null);
    }
  };

  // Fetch data
  useEffect(() => {
    let mounted = true;
    const loadCoupons = async () => {
      try {
        setLoadingCoupons(true);
        const data = await fetchMemberCoupons();
        if (mounted) setCoupons(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load coupons', e);
        if (mounted) setCoupons([]);
      } finally {
        if (mounted) setLoadingCoupons(false);
      }
    };

    loadCoupons();
    // initial points load for page 0
    loadPoints(0);
    // load expiring summary once userId is available
    if (user?.userId) {
      loadExpiring(user.userId);
    }
    return () => {
      mounted = false;
    };
  }, []);

  // Re-fetch expiring points if userId changes
  useEffect(() => {
    if (user?.userId) {
      loadExpiring(user.userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  useEffect(() => {
    // reload points when page changes
    loadPoints(pointsPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsPage]);

  const handleTabChange = (_: unknown, value: 'coupon' | 'points') => {
    setActiveTab(value);
  };

  const handleQrCodeClick = () => {
    setQrCodeModalOpen(true);
  };

  const handleCloseQrCodeModal = () => {
    setQrCodeModalOpen(false);
  };

  const handleCouponClick = () => {
    setCouponModalOpen(true);
  };

  const handleCloseCouponModal = () => {
    setCouponModalOpen(false);
  };

  // const handleSignOut = async () => {
  //   try {
  //     await signOut();
  //     navigate(routes.login);
  //   } catch (error) {
  //     console.error("Error signing out:", error);
  //     navigate(routes.login);
  //   }
  // };

  useEffect(() => {
    // Generate QR code when modal opens
    if (qrCodeModalOpen && user?.membershipCode) {
      QRCode.toDataURL(`${user?.membershipCode}`, {
        width: 200,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      })
        .then(url => {
          setQrCodeDataUrl(url);
        })
        .catch(err => {
          console.error("Error generating QR code:", err);
        });
    }
  }, [qrCodeModalOpen]);

  useEffect(() => {
    // Generate QR code for coupon modal when it opens and we have a selected coupon
    if (couponModalOpen && selectedCoupon) {
      const memberPhone = (user as any)?.phone || (user as any)?.phoneNumber || (user as any)?.mobile || "";
      const couponCode = selectedCoupon.code || "";
      const couponId = selectedCoupon.id || (selectedCoupon as any).couponId || "";
      const value = `C1|${memberPhone}|${couponCode}|$${couponId}`;
      QRCode.toDataURL(value, {
        width: 200,
        margin: 2,
        color: { dark: "#000000", light: "#FFFFFF" },
      })
        .then((url) => setCouponQrDataUrl(url))
        .catch((err) => {
          console.error("Error generating coupon QR code:", err);
          setCouponQrDataUrl("");
        });
    } else if (!couponModalOpen) {
      // Reset when modal closes
      setCouponQrDataUrl("");
    }
  }, [couponModalOpen, selectedCoupon, user]);

  // Derived fields for selected coupon (used in modal)
  const selected = selectedCoupon;
  const fmtDate = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };
  const startIso = selected?.startsAt ?? null;
  const endIso = selected?.expiresAt ?? (selected as any)?.validTo ?? null;
  const start = startIso ? new Date(startIso) : undefined;
  const now = new Date();
  const end = endIso ? new Date(endIso) : undefined;
  const upcoming = !!(start && now < start);
  const isValidNow = (!start || start <= now) && (!end || end >= now);
  const rawStatus = selected?.status ? selected.status.toLowerCase() : '';
  const statusText = startIso ? `${t('home.coupons.will')} ${rawStatus} ${t('home.coupons.on')} ${fmtDate(startIso)}` : rawStatus;
  const dateRangeText = startIso ? `${fmtDate(startIso)} ${t('home.coupons.to')} ${endIso ? ` ${fmtDate(endIso)}` : '01-01-9999'}` : '';
  const headerDateText = upcoming ? statusText : isValidNow ? '' : dateRangeText;
  const headerDateColor = startIso ? '#09933E' : '#878787';
  const couponTitleText = selected?.name || '';
  const couponDescription = selected?.description || selected?.promotionDescription || '';
  const expireDateText = fmtDate(selected?.expiresAt ?? (selected as any)?.validTo ?? null);
  const couponTerms = (() => {
    if (!selected) return '';
    if (locale === 'cn') return selected.importantNoticeCn || selected.termsAndConditions || '';
    if (locale === 'ja') return selected.importantNoticeZh || selected.termsAndConditions || '';
    return selected.importantNotice || selected.termsAndConditions || '';
  })();

  const content = (
    <Box
      sx={{
        minHeight: isDesktop ? "auto" : "100vh",
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top Bar / Header */}
      <Box
        sx={{
          backgroundColor: "white",
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 10,
        }}
      >
        {/* Left side - Logo and store name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={storeSettings?.logoUrl || '/icons/logo.svg'}
            alt={storeSettings?.text || "Logo"}
            style={{
              maxWidth: "40px",
              maxHeight: "40px",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Right side - Language selection, history icon, and logout */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Language selection icon */}
          <LanguageSelector />
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          py: isDesktop ? 10 : 0,
          display: "flex",
          flexDirection: "column",
          ...(isDesktop && {
            flexDirection: "column",
            gap: 3,
          }),
          backgroundColor: "#FCFCFC",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            px: 2,
            pb: 2.5,
            borderRadius: "0 0 48px 48px",
            background: "#FFF",
            boxShadow: "0 0 8px 0 rgba(0, 0, 0, 0.15)",
          }}>
          {/* GRINDIE Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              color: "white",
              position: "relative",
              overflow: "hidden",
              ...(isDesktop && {
                width: "100%",
              }),
              borderRadius: "8px",
              background: "conic-gradient(from 134deg at 50% 50%, #E94E1B 0deg, #DF5E37 103.84615302085876deg, #F5DACE 360deg)",
              boxShadow: "1px 2px 28.9px 0 #D8D8D8",
            }}
          >
            {/* Top section with GRINDIE text and QR code */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 6,
                position: "relative",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "white",
                  fontSize: "16px",
                }}
              >
                {user?.membershipLevelName || user?.firstName || 'Member'}
              </Typography>
              <Box sx={{
                backgroundColor: 'white',
                p: 1,
                position: 'absolute',
                right: '-24px',
                borderRadius: '8px 0 0 8px',
                height: '38px',
              }}>
                <img
                  src="/icons/qr-code.svg"
                  alt="QR Code"
                  style={{
                    width: "20px",
                    height: "20px",
                    cursor: "pointer",
                  }}
                  onClick={handleQrCodeClick}
                />
              </Box>
            </Box>
            {/* Bottom section with stamp info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: "white",
                    fontSize: 28,
                    fontWeight: 800,
                    lineHeight: 1.1,
                  }}
                >
                  {(user?.pointsBalance ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="h6" sx={{
                  color: "#FFF",
                  fontSize: "12px",
                  fontWeight: 600,
                  opacity: 0.9,
                }}>
                </Typography>
                {firstExpiring ? (
                  <Typography sx={{ color: '#FFF', fontSize: '10px', fontWeight: 500, mt: 0.75 }}>
                    {t('home.points.expiring')
                      .replace('{points}', (firstExpiring.pointsExpiring ?? 0).toLocaleString())
                      .replace('{date}', firstExpiring.expiryDate)}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Paper>
          {user?.nextMembershipLevel && (
            <Box sx={{
              px: 2,
              color: 'primary.main',
            }}>
              <LinearProgress variant="determinate" value={Math.max(user?.nextMembershipLevel?.progressToNextLevel, 5)} color="inherit" />
              <Typography sx={{
                color: '#000',
                fontSize: 13,
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                mt: 1,
              }}>
                {(() => {
                  const translatedText = t('home.progressToNextLevel')
                    .replace('{nextLevelName}', user?.nextMembershipLevel?.nextMembershipLevelName ?? '');
                  const parts = translatedText.split('{amount}');
                  return (
                    <>
                      {parts[0]}
                      <Typography variant="caption" sx={{
                        color: "primary.main",
                        fontWeight: 700,
                      }}>
                        ${user?.nextMembershipLevel?.requiredSpendingAmountToNextLevel?.toLocaleString() ?? '0'}
                      </Typography>
                      {parts[1]}
                    </>
                  );
                })()}
              </Typography>
            </Box>
          )}
        </Box>
        {/* Coupons / Points Tabs */}
        <Paper elevation={0} sx={{ p: 2.5, backgroundColor: 'transparent' }}>
          {/* Referral Section on Home (conditional) */}
          {(() => {
            const referral = storeSettings?.referralSettings;
            const hasReferrerPoints = Boolean(referral?.referrerPointsEnabled && (referral?.referrerPointsAmount ?? null) != null);
            const hasNewCustomerPoints = Boolean(referral?.newCustomerPointsEnabled && (referral?.newCustomerPointsAmount ?? null) != null);
            const shouldShowReferral = Boolean(referral && (referral.enabled) && (hasReferrerPoints || hasNewCustomerPoints));
            return shouldShowReferral;
          })() && (
              <Paper elevation={0} sx={{
                p: 2.5,
                width: "100%",
                borderRadius: "10px",
                backgroundColor: "#FFF",
                boxShadow: "0 0 8px 0 rgba(0, 0, 0, 0.10)",
              }}>
                <Box sx={{
                  display: "inline-flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "39px",
                  width: "100%",
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{
                      color: "#000",
                      fontSize: 14,
                      fontWeight: 600,
                    }}>{t('home.referFriend.title')}</Typography>
                  </Box>
                  <Box sx={{ pl: 1, fontSize: 16 }}>
                    {(() => {
                      const r = storeSettings?.referralSettings;
                      const hasRef = Boolean(r?.referrerPointsEnabled && (r?.referrerPointsAmount ?? null) != null);
                      const hasNew = Boolean(r?.newCustomerPointsEnabled && (r?.newCustomerPointsAmount ?? null) != null);
                      const refPts = r?.referrerPointsAmount ?? 0;
                      const newPts = r?.newCustomerPointsAmount ?? 0;

                      if (hasRef && hasNew) {
                        return (
                          <Typography
                            variant="body2"
                            sx={{ color: "#858585", fontSize: 16, fontWeight: 500, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'pre-line' }}
                            onClick={() => { setShowCoupon(true); }}
                          >
                            {t('home.referFriend.both')
                              .replace('{referrer}', String(refPts))
                              .replace('{newCustomer}', String(newPts))}
                          </Typography>
                        );
                      }

                      if (hasRef) {
                        return (
                          <Typography
                            variant="body2"
                            sx={{ color: "#858585", fontSize: 16, fontWeight: 500, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                            onClick={() => { setShowCoupon(true); }}
                          >
                            {t('home.referFriend.getPoints').replace('{points}', String(refPts))}
                          </Typography>
                        );
                      }

                      if (hasNew) {
                        return (
                          <Typography
                            variant="body2"
                            sx={{ color: "#858585", fontSize: 16, fontWeight: 500, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                            onClick={() => { setShowCoupon(true); }}
                          >
                            {t('home.referFriend.friendsGetPoints').replace('{points}', String(newPts))}
                          </Typography>
                        );
                      }

                      return null;
                    })()}
                  </Box>
                </Box>
                {showCoupon ? (
                  <Box sx={{
                    width: '100%',
                    pt: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        fullWidth
                        placeholder='Enter coupon code'
                        value={coupon}
                        onChange={(e) => setCoupon(e.target.value)}
                        disabled
                        size="small"
                        sx={{
                          width: '160px',
                        }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => console.log('Copy')}
                      >
                        Copy
                      </Button>
                    </Box>
                    <Box sx={{
                      textAlign: 'center',
                      mt: 1.5,
                    }}>
                      <Typography sx={{
                        color: "#000",
                        fontSize: 13,
                        fontWeight: 500,
                        mb: 2
                      }}>
                        {t('home.referFriend.copyAndSend')}
                      </Typography>
                      <Typography variant="caption"
                        sx={{
                          color: "primary.main",
                          fontSize: '13px',
                          fontWeight: 500,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={() => setShowCoupon(false)}
                      >Done</Typography>
                    </Box>
                  </Box>
                ) : null}
              </Paper>
            )}

          <Box sx={{ mt: 1.5 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              textColor="inherit"
              TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}
            >
              <Tab value="coupon" label={t('home.tabs.myCoupon')} sx={{ textTransform: 'none', fontWeight: 600 }} />
              <Tab value="points" label={t('home.tabs.points')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            </Tabs>
          </Box>
          <Divider />
          <Box sx={{ pt: 3 }}>
            {activeTab === 'coupon' && (
              <Box>
                {loadingCoupons ? (
                  <Typography sx={{ color: '#666', px: 2 }}>{t('home.coupons.title')}...</Typography>
                ) : coupons.length === 0 ? (
                  <Typography sx={{ color: '#888', px: 2 }}>{t('home.coupons.empty')}</Typography>
                ) : (
                  <Box>
                    {/*<Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                      {CouponTypes.map(({ value, label }) => (
                        <Button
                          key={value}
                          variant={value === couponFilter ? "contained" : "outlined"}
                          size="small"
                          onClick={() => setCouponFilter(value)}
                          sx={value === couponFilter ? {
                            flex: 1,
                            borderRadius: '100px',
                          } : {
                            flex: 1,
                            borderRadius: '100px',
                            border: '1px solid #F2F2F2',
                            background: 'white',
                            color: '#878787',
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Box>*/}
                    <List sx={{ width: '100%', py: 0 }}>
                      {coupons.map((c) => {
                        const isPercent = (c.discountType || '').toLowerCase() === 'percent';
                        const badgeText = (() => {
                          if (isPercent && c.discountPercent != null) {
                            const pctNum = typeof c.discountPercent === 'number' ? c.discountPercent : Number(c.discountPercent);
                            const raw = Number.isFinite(pctNum)
                              ? (Number.isInteger(pctNum) ? pctNum.toString() : pctNum.toFixed(2))
                              : '';
                            return raw.replace(/\.0+$/, '') + '%';
                          }
                          if (c.discountAmountCents != null) {
                            return `$${(c.discountAmountCents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
                          }
                          return c.amountLabel || '';
                        })();
                        const titleText = c.name || '';
                        const startAt = c.startsAt ?? null;
                        const fmtDate = (iso?: string) => {
                          if (!iso) return '';
                          const d = new Date(iso);
                          const dd = String(d.getDate()).padStart(2, '0');
                          const mm = String(d.getMonth() + 1).padStart(2, '0');
                          const yyyy = d.getFullYear();
                          return `${dd}-${mm}-${yyyy}`;
                        };

                        const startIso = startAt;
                        const endIso = c.expiresAt ?? c.validTo ?? null;

                        const start = startIso ? new Date(startIso) : undefined;
                        const now = new Date();
                        const end = endIso ? new Date(endIso) : undefined;

                        const upcoming = !!(start && now < start);
                        const isValidNow = (!start || start <= now) && (!end || end >= now);

                        const rawStatus = c.status ? c.status.toLowerCase() : '';
                        const statusText = startIso
                          ? `${t('home.coupons.will')} ${rawStatus} ${t('home.coupons.on')} ${fmtDate(startIso)}`
                          : rawStatus;

                        const dateRangeText =
                          startIso ? `${fmtDate(startIso)} ${t('home.coupons.to')} ${endIso ? ` ${fmtDate(endIso)}` : '01-01-9999'}` : '';

                        return (
                          <ListItem key={c.id || c.couponId} disablePadding sx={{ mb: 3 }}>
                            <ListItemButton onClick={() => { setSelectedCoupon(c); handleCouponClick(); }} sx={{
                              p: 0,
                              backgroundColor: 'white',
                              '&:hover': {
                                backgroundColor: 'transparent',
                              }
                            }}>
                              <Paper elevation={0} sx={{
                                width: '100%',
                                backgroundColor: 'transparent',
                                boxShadow: '0 0 14px 0 rgba(0, 0, 0, 0.10)',
                              }}>
                                <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', p: 2 }}>
                                  {/* Left badge */}
                                  <Box sx={{
                                    width: '72px', height: '72px',
                                    borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    backgroundImage: 'url(/seal.svg)',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',

                                  }}>
                                    <Typography sx={{
                                      color: 'primary.main',
                                      textAlign: 'center',
                                      fontFamily: "Times New Roman",
                                      fontSize: 30.75,
                                      fontStyle: 'normal',
                                      fontWeight: 400,
                                    }}>{badgeText}</Typography>
                                  </Box>
                                  {/* Middle content */}
                                  <Box sx={{ flex: 1 }}>
                                    <Typography sx={{
                                      color: '#000',
                                      fontSize: 16,
                                      fontWeight: 600,
                                    }}>{titleText}</Typography>
                                  </Box>
                                  {/* Optional right image */}
                                  {c.imageUrl && (
                                    <img src={c.imageUrl} alt={titleText} style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover' }} />
                                  )}</Box>

                                <Box
                                  sx={{
                                    position: 'relative',
                                    height: '1px',
                                    background:
                                      'repeating-linear-gradient(to right, #F2F2F2 0, #F2F2F2 2px, transparent 2px, transparent 4px)',
                                    '&::before, &::after': {
                                      content: '""',
                                      position: 'absolute',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      width: 6,
                                      height: 12,
                                      backgroundColor: '#F2F2F2',
                                    },
                                    '&::before': {
                                      left: 0,
                                      borderRadius: '0 6px 6px 0',
                                    },
                                    '&::after': {
                                      right: 0,
                                      borderRadius: '6px 0 0 6px',
                                    },
                                  }}
                                />
                                <Typography sx={{
                                  color: startAt ? '#09933E' : '#878787',
                                  textAlign: 'center',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  p: '10px'
                                }}>
                                  {upcoming ? statusText : isValidNow ? null : dateRangeText}
                                </Typography>
                              </Paper>
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}
              </Box>
            )}
            {activeTab === 'points' && (
              <Box>
                {loadingPoints ? (
                  <Typography sx={{ color: '#666', px: 2 }}>{t('home.points.title')}...</Typography>
                ) : !pointsResp || pointsResp.content.length === 0 ? (
                  <Typography sx={{ color: '#888', px: 2 }}>{t('home.points.empty')}</Typography>
                ) : (
                  <>
                    <List sx={{ py: 0 }}>
                      {pointsResp.content.map((p) => (
                        <ListItem key={p.transactionId || p.id} sx={{ p: 0, mb: 1.5 }}>
                          <ListItemText
                            primary={
                              <Box className="coupon-points-frame" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#888' }}>
                                  {new Date(p.createdAt).toLocaleDateString()}
                                </Typography>
                                {(() => {
                                  const pres = getPointPresentation(p); return (
                                    <Typography sx={{ fontWeight: 600, color: pres.positive ? 'primary.main' : '#7B7B7B' }}>
                                      {`${pres.sign}${pres.value} points`}
                                    </Typography>
                                  );
                                })()}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                  {p.action}
                                </Typography>
                                {p.description && (
                                  <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                                    {p.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{
                              borderRadius: '8px',
                              background: 'white',
                              boxShadow: '0 0 8px 0 rgba(0, 0, 0, 0.10)',
                              p: 1.5,
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    {/* Pagination controls */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={pointsResp.first}
                        onClick={() => setPointsPage((p) => Math.max(0, p - 1))}
                        sx={{ textTransform: 'none', borderColor: '#A9C191', color: '#A9C191' }}
                      >
                        {t('common.prev')}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={pointsResp.last}
                        onClick={() => setPointsPage((p) => p + 1)}
                        sx={{ textTransform: 'none', borderColor: '#A9C191', color: '#A9C191' }}
                      >
                        {t('common.next')}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box >


      {/* QR Code Modal */}
      <Dialog
        open={qrCodeModalOpen}
        onClose={handleCloseQrCodeModal}
        fullScreen={!isDesktop}
        PaperProps={{
          sx: {
            borderRadius: isDesktop ? "16px" : "24px",
            maxWidth: "90vw",
            width: "320px",
            margin: isDesktop ? "auto" : "0",
            position: isDesktop ? "relative" : "fixed",
            minHeight: "42vh",
            height: 'auto',
            backgroundColor: "white",
            overflow: "visible",
          },
        }}
      >
        {/* Close Button (X) on bottom center */}
        {!isDesktop &&
          <IconButton
            onClick={handleCloseQrCodeModal}
            sx={{
              position: "absolute",
              bottom: '-24px',
              left: '50%',
              transform: 'translateX(-50%) translateY(100%)',
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.06)",
              },
              backgroundColor: "white",
              borderRadius: "50%",
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
        }
        <DialogContent sx={{
          p: 0,
          backgroundColor: 'white',
          position: "relative",
          borderRadius: isDesktop ? "16px" : "24px",
        }}>

          {/* Header */}
          <Box
            sx={{
              backgroundColor: "white",
              color: "black",
              p: 2,
              borderTopLeftRadius: isDesktop ? "16px" : "24px",
              borderTopRightRadius: isDesktop ? "16px" : "24px",
              position: "relative",
            }}
          >
            {/* Close Button (X) on top right */}
            {isDesktop &&
              <IconButton
                onClick={handleCloseQrCodeModal}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.06)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            }

            <Typography
              sx={{ textAlign: "center", fontWeight: 600, color: "#000", fontSize: "16px" }}
            >
              {t("qrCode.title")}
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, textAlign: "center", backgroundColor: "white" }}>
            {/* QR Code */}
            <Box>
              {qrCodeDataUrl && (
                <img
                  src={qrCodeDataUrl}
                  alt="QR Code"
                  style={{
                    width: "200px",
                    height: "200px",
                    borderRadius: "8px",
                  }}
                />
              )}
            </Box>

            {/* Member Number */}
            <Typography
              variant="body1"
              sx={{
                color: "#858080",
                textAlign: "center",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 500,
                mb: '50px',
              }}
            >
              {user?.membershipCode}
            </Typography>

            {/* Add to Wallet Button */}
            <Button
              variant="text"
              onClick={() => {
                navigate(routes.addToWallet);
              }}
              sx={{
                backgroundColor: "transparent",
                textTransform: "none",
                p: 0,
                minHeight: 'unset',
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <img
                src="/icons/ic_apple_wallet.svg"
                alt="Add to Apple Wallet"
                style={{ height: 30 }}
              />
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* QR Coupon Modal */}
      <Dialog
        open={couponModalOpen}
        onClose={handleCloseCouponModal}
        fullScreen={!isDesktop}
        PaperProps={{
          sx: {
            borderTopLeftRadius: "28px",
            borderTopRightRadius: "28px",
            maxWidth: isDesktop ? "90vw" : "100%",
            width: isDesktop ? "320px" : "100%",
            margin: isDesktop ? "auto" : "0",
            position: isDesktop ? "relative" : "fixed",
            bottom: isDesktop ? "auto" : 0,
            maxHeight: isDesktop ? "auto" : "90vh",
            minHeight: "42vh",
            height: 'auto',
            backgroundColor: "white",
          },
        }}
      >
        <DialogContent sx={{
          p: 0,
          backgroundColor: 'white',
          position: "relative",
          borderRadius: isDesktop ? "16px" : "24px",
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header */}
          <Box
            sx={{
              backgroundColor: "white",
              color: "black",
              p: isDesktop ? 1 : '50px',
              borderTopLeftRadius: isDesktop ? "16px" : "24px",
              borderTopRightRadius: isDesktop ? "16px" : "24px",
              position: "relative",
              borderRadius: "20px 20px 0 0",
              background: "#404040",
            }}
          >
            {/* Close Button (X) on top right */}
            <IconButton
              onClick={handleCloseCouponModal}
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                color: "white",
                '&:hover': { backgroundColor: "rgba(0, 0, 0, 0.06)" },
              }}
            >
              <CloseIcon />
            </IconButton>

            <Box sx={{ textAlign: "center" }}>
              <Paper elevation={0} sx={{
                width: '100%',
                backgroundColor: 'white',
                boxShadow: 'none',
                borderRadius: 2,
                pt: 3,
              }}>

                {couponQrDataUrl && (
                  <img
                    src={couponQrDataUrl}
                    alt="QR Code"
                    style={{ width: "200px", height: "200px", borderRadius: "8px" }}
                  />
                )}
                <Typography
                  sx={{
                    color: "#878787",
                    textAlign: "center",
                    fontSize: "10px",
                    fontWeight: 400,
                    lineHeight: 1,
                    mb: 1.5,
                  }}
                >
                  Present this QR code to the merchant
                </Typography>

                <Box
                  sx={{
                    position: 'relative',
                    height: '1px',
                    background:
                      'repeating-linear-gradient(to right, #F2F2F2 0, #F2F2F2 2px, transparent 2px, transparent 4px)',
                    '&::before, &::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 6,
                      height: 12,
                      backgroundColor: '#404040',
                    },
                    '&::before': { left: '-1px', borderRadius: '0 6px 6px 0' },
                    '&::after': { right: '-1px', borderRadius: '6px 0 0 6px' },
                  }}
                />
                <Typography sx={{
                  textAlign: "center",
                  fontSize: 12,
                  fontWeight: 500,
                  mt: 1.5,
                  color: headerDateColor,
                }}>
                  {headerDateText || ' '}
                </Typography>
                <Typography sx={{
                  color: 'black',
                  textAlign: "center",
                  fontSize: 16,
                  fontWeight: 600,
                  p: '10px 46px 25px',
                }}>
                  {couponTitleText}
                </Typography>
              </Paper>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, backgroundColor: "white", display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflowY: 'auto' }}>
            <Box>
              <Typography sx={{
                color: 'primary.main',
                fontFamily: 'Montserrat',
                fontSize: 14,
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: '20px', /* 142.857% */
                mb: 0.5,
              }}>Coupon Details</Typography>
              <Typography sx={{
                color: 'black',
                fontSize: 16,
                fontWeight: 600,
                mb: 0.5,
              }}>
                {couponTitleText}
              </Typography>
              {couponDescription ? (
                <Typography sx={{
                  color: '#878787',
                  fontSize: 14,
                  fontWeight: 400,
                  mb: 0.5,
                }}>
                  {couponDescription}
                </Typography>
              ) : null}
            </Box>
            {expireDateText ? (
              <Box>
                <Typography sx={{
                  color: 'primary.main',
                  fontFamily: 'Montserrat',
                  fontSize: 14,
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: '20px', /* 142.857% */
                  mb: 0.5,
                }}>Expire Date</Typography>
                <Typography sx={{
                  color: '#878787',
                  fontSize: 14,
                  fontWeight: 400,
                  mb: 0.5,
                }}>
                  {expireDateText}
                </Typography>
              </Box>
            ) : null}
            {couponTerms ? (
              <Box>
                <Typography sx={{
                  color: 'primary.main',
                  fontFamily: 'Montserrat',
                  fontSize: 14,
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: '20px', /* 142.857% */
                  mb: 0.5,
                }}>
                  Terms & Conditions
                </Typography>
                <Typography sx={{
                  color: '#878787',
                  fontSize: 14,
                  fontWeight: 400,
                  mb: 0.5,
                  whiteSpace: 'pre-line',
                }}>
                  {couponTerms}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
      </Dialog >
    </Box >
  );
  // If it's desktop, wrap in background and dialog
  if (isDesktop) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          background:
            'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("/images/background.png")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "60%",
            backgroundColor: "white",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }
  // If it's mobile, return the content directly
  return (
    <>
      {content}
    </>
  );
}
